"use client";

import React, { useRef, useEffect } from "react";

export type OtpInputProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
};

export default function OtpInput({
  value,
  onChange,
  disabled = false,
  onComplete,
  autoFocus = true,
}: OtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index: number, val: string) => {
    const cleanVal = val.replace(/\D/g, "");
    if (!cleanVal) {
      // Clear current digit
      const nextDigits = [...digits];
      nextDigits[index] = "";
      const nextOtp = nextDigits.join("");
      onChange(nextOtp);
      return;
    }

    if (cleanVal.length === 1) {
      const nextDigits = [...digits];
      nextDigits[index] = cleanVal;
      const nextOtp = nextDigits.join("");
      onChange(nextOtp);

      if (index < 5 && inputsRef.current[index + 1]) {
        inputsRef.current[index + 1]?.focus();
      }

      if (nextOtp.length === 6 && onComplete) {
        onComplete(nextOtp);
      }
    } else if (cleanVal.length > 1) {
      // Pasted or typed multi-character string
      handlePasteData(cleanVal);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        const nextDigits = [...digits];
        nextDigits[index - 1] = "";
        const nextOtp = nextDigits.join("");
        onChange(nextOtp);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePasteData = (pastedText: string) => {
    const cleanPasted = pastedText.replace(/\D/g, "").slice(0, 6);
    if (!cleanPasted) return;

    onChange(cleanPasted);

    const focusIndex = Math.min(cleanPasted.length, 5);
    inputsRef.current[focusIndex]?.focus();

    if (cleanPasted.length === 6 && onComplete) {
      onComplete(cleanPasted);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    handlePasteData(pasted);
  };

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 dir-ltr" dir="ltr">
      {Array.from({ length: 6 }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[index] || ""}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className="h-12 w-10 sm:h-14 sm:w-12 rounded-xl border border-border bg-surface text-center text-xl font-bold text-primary outline-none transition focus:border-primary focus:bg-card focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}
